import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, ShoppingBag, Menu, X, LogOut } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import Logo from "@/components/Logo";

const NAV = [
  { label: "Shop", to: "/shop" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

const getCustomer = () => {
  try { return JSON.parse(localStorage.getItem("adc_customer")); } catch { return null; }
};

export const Navbar = () => {
  const { count, setOpen } = useCart();
  const [searchOpen, setSearchOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [mobile, setMobile] = useState(false);
  const [customer, setCustomer] = useState(getCustomer());
  const navigate = useNavigate();

  useEffect(() => {
    const sync = () => setCustomer(getCustomer());
    window.addEventListener("adc-auth-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("adc-auth-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const signOut = () => {
    localStorage.removeItem("adc_customer");
    window.dispatchEvent(new Event("adc-auth-change"));
    setMobile(false);
    toast.success("Signed out");
    navigate("/");
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (term.trim()) {
      navigate(`/shop?search=${encodeURIComponent(term.trim())}`);
      setSearchOpen(false);
      setTerm("");
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-black border-b border-white/10" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-24 flex items-center justify-between">
        <button
          className="md:hidden text-cream"
          onClick={() => setMobile((m) => !m)}
          data-testid="mobile-menu-btn"
          aria-label="Menu"
        >
          {mobile ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>

        <Logo />

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className="font-body uppercase tracking-[0.18em] text-xs text-cream/80 hover:text-gold transition-colors duration-300 relative after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-gold hover:after:w-full after:transition-all after:duration-300"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-5 text-cream">
          <button onClick={() => setSearchOpen((s) => !s)} data-testid="search-toggle" aria-label="Search">
            <Search className="w-5 h-5 hover:text-gold transition-colors" strokeWidth={1.5} />
          </button>
          <Link to="/account" data-testid="account-link" aria-label="Account">
            <User className="w-5 h-5 hover:text-gold transition-colors" strokeWidth={1.5} />
          </Link>
          {customer?.user && (
            <button onClick={signOut} data-testid="navbar-signout" aria-label="Sign out" title="Sign out">
              <LogOut className="w-5 h-5 hover:text-gold transition-colors" strokeWidth={1.5} />
            </button>
          )}
          <button onClick={() => setOpen(true)} className="relative" data-testid="cart-toggle" aria-label="Cart">
            <ShoppingBag className="w-5 h-5 hover:text-gold transition-colors" strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-gold text-black text-[10px] font-semibold rounded-full w-4.5 h-4.5 min-w-[18px] h-[18px] flex items-center justify-center px-1"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-white/10 bg-black/95">
          <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
            <Search className="w-5 h-5 text-gold" strokeWidth={1.5} />
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Search our seductive confections…"
              data-testid="search-input"
              className="flex-1 bg-transparent border-0 outline-none font-serif text-lg text-cream placeholder:text-white/30"
            />
          </form>
        </div>
      )}

      {mobile && (
        <nav className="md:hidden border-t border-white/10 bg-black/95 px-6 py-4 flex flex-col gap-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onClick={() => setMobile(false)}
              data-testid={`mobile-nav-${n.label.toLowerCase()}`}
              className="font-body uppercase tracking-[0.18em] text-sm text-cream/80 hover:text-gold"
            >
              {n.label}
            </Link>
          ))}
          <Link to="/account" onClick={() => setMobile(false)} data-testid="mobile-nav-account"
            className="font-body uppercase tracking-[0.18em] text-sm text-cream/80 hover:text-gold">
            Account
          </Link>
          {customer?.user && (
            <button onClick={signOut} data-testid="mobile-signout"
              className="inline-flex items-center gap-2 font-body uppercase tracking-[0.18em] text-sm text-cream/80 hover:text-gold text-left">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          )}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
